'use client';

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole =
  // Full access to everything (Admin also gets system administration)
  | 'Admin' | 'Manager'
  // Restricted to the sales surfaces (boards, customers, contacts)
  | 'Sales';

export const ALL_USER_ROLES: UserRole[] = ['Admin', 'Manager', 'Sales'];

// Roles that always have access to every page (Sales is the only restricted role)
export const ALL_ACCESS_ROLES: UserRole[] = ['Admin', 'Manager'];

// Display labels (for UI dropdowns, badges, etc.)
export const ROLE_DISPLAY_LABELS: Record<UserRole, string> = {
  Admin: 'Admin',
  Manager: 'Manager',
  Sales: 'Sales',
};

export interface MockUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: UserRole; // primary role (first in roles array)
  roles: UserRole[]; // all assigned roles
  profile_image: string;
  assigned_pm_id: string;
}

interface AuthContextType {
  currentUser: MockUser;
  setCurrentUser: (user: MockUser) => void;
  // Legacy single-role helpers (kept for backward compat)
  isSuperAdmin: boolean;
  isProjectManager: boolean;
  isInstallerCrew: boolean;
  canSeeExecutive: boolean;
  canSeeFinancial: boolean;
  canSeeOperations: boolean;
  // Multi-role helpers
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isAuthenticated: boolean;
  authLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ role: UserRole | null; error: string | null }>;
  signOut: () => Promise<void>;
  // Effective identity — respects View As impersonation
  effectiveUserId: string | null;
  effectiveRoles: UserRole[];
}

// Login is restricted to company accounts. getnextroof.com is the canonical
// domain; openbooq.com is a TEMPORARY transition domain so existing accounts
// (still on the old domain) are not locked out mid-migration. Remove
// 'openbooq.com' once every login has moved to @getnextroof.com.
export const ALLOWED_EMAIL_DOMAIN = 'getnextroof.com';
export const ALLOWED_EMAIL_DOMAINS = ['getnextroof.com', 'openbooq.com'];
export const isAllowedEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const e = email.trim().toLowerCase();
  return ALLOWED_EMAIL_DOMAINS.some((d) => e.endsWith(`@${d}`));
};

// Mock users for simulation / fallback
export const MOCK_USERS: MockUser[] = [
  {
    id: 'pm-john-001',
    first_name: 'John',
    last_name: 'D.',
    email: 'john.doe@getnextroof.com',
    role: 'Admin',
    roles: ['Admin'],
    profile_image: '',
    assigned_pm_id: 'pm-john-001',
  },
];

// Empty placeholder user — shown only while auth is resolving (authLoading=true)
// Never shown to the end user because RouteGuard blocks rendering until authLoading=false
const EMPTY_USER: MockUser = {
  id: '',
  first_name: '',
  last_name: '',
  email: '',
  role: 'Admin',
  roles: ['Admin'],
  profile_image: '',
  assigned_pm_id: '',
};

const AuthContext = createContext<AuthContextType>({
  currentUser: EMPTY_USER,
  setCurrentUser: () => {},
  isSuperAdmin: false,
  isProjectManager: false,
  isInstallerCrew: false,
  canSeeExecutive: false,
  canSeeFinancial: false,
  canSeeOperations: false,
  hasRole: () => false,
  hasAnyRole: () => false,
  isAuthenticated: false,
  authLoading: true,
  signIn: async () => ({ role: null, error: null }),
  signOut: async () => {},
  effectiveUserId: null,
  effectiveRoles: [],
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<MockUser>(EMPTY_USER);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  // Prevents the SIGNED_IN event handler from running loadUserProfile
  // concurrently while signIn() is already handling the profile fetch.
  const signingInRef = useRef(false);

  const supabase = createClient();

  useEffect(() => {
    // Safety timeout: if INITIAL_SESSION never fires, unblock the app after 5s
    const safetyTimer = setTimeout(() => {
      setAuthLoading((prev) => {
        if (prev) {
          // Auth never resolved — treat as unauthenticated, do NOT default to mock user
          setIsAuthenticated(false);
          return false;
        }
        return prev;
      });
    }, 5000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'INITIAL_SESSION') {
        clearTimeout(safetyTimer);
        if (session?.user && !isAllowedEmail(session.user.email)) {
          // Restored session belongs to a non-company account — reject it.
          await supabase.auth.signOut();
          setCurrentUser(EMPTY_USER);
          setIsAuthenticated(false);
        } else if (session?.user) {
          await loadUserProfile(session.user.id, session.user.email || '');
        } else {
          setCurrentUser(EMPTY_USER);
          setIsAuthenticated(false);
        }
        setAuthLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(EMPTY_USER);
        setIsAuthenticated(false);
      }
      // SIGNED_IN intentionally not handled here. Fresh logins go through signIn(),
      // which loads the profile directly. Page refreshes hit INITIAL_SESSION above.
      // Handling SIGNED_IN here would re-fetch the profile and race the redirect.
    });

    return () => subscription.unsubscribe();
  }, []);

  // Shared helper: fetch user_profiles via the server route which uses
  // SUPABASE_SERVICE_ROLE_KEY to bypass RLS. Eliminates the cookie-propagation
  // race that the previous direct .from('user_profiles') queries hit.
  const fetchProfile = async (
    fallbackEmail?: string,
  ): Promise<{ profile: any | null; error: string | null }> => {
    try {
      const response = await fetch('/api/auth/load-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fallbackEmail: fallbackEmail || '' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          profile: null,
          error: data?.error || `Failed to load profile (${response.status})`,
        };
      }
      return { profile: data.profile ?? null, error: data.error ?? null };
    } catch (err: any) {
      return {
        profile: null,
        error: err?.message || 'Network error loading profile',
      };
    }
  };

  const loadUserProfile = async (userId: string, email: string) => {
    const { profile, error } = await fetchProfile(email);
    if (error || !profile) {
      console.warn('[AuthContext] loadUserProfile failed:', error || 'no profile returned');
      setCurrentUser(EMPTY_USER);
      setIsAuthenticated(false);
      return;
    }

    const rolesArray: UserRole[] =
      Array.isArray(profile.user_roles) && profile.user_roles.length > 0
        ? (profile.user_roles as UserRole[])
        : profile.user_role
          ? [profile.user_role as UserRole]
          : [];

    if (rolesArray.length === 0) {
      console.warn('[AuthContext] loadUserProfile: profile has no roles');
      setCurrentUser(EMPTY_USER);
      setIsAuthenticated(false);
      return;
    }

    setCurrentUser({
      id: profile.id || userId,
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      email: profile.email || email,
      role: rolesArray[0],
      roles: rolesArray,
      profile_image: profile.profile_image || '',
      assigned_pm_id: profile.assigned_pm_id || profile.id || userId,
    });
    setIsAuthenticated(true);
  };

  const signIn = async (
    email: string,
    password: string,
  ): Promise<{ role: UserRole | null; error: string | null }> => {
    signingInRef.current = true;
    try {
      // Domain gate: only @getnextroof.com company accounts may sign in.
      if (!isAllowedEmail(email)) {
        return {
          role: null,
          error: `Access is restricted to @${ALLOWED_EMAIL_DOMAIN} accounts. Please sign in with your NextRoof 360 work email.`,
        };
      }

      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        if (
          error.message.includes('Invalid login credentials') ||
          error.message.includes('invalid_credentials')
        ) {
          return {
            role: null,
            error: 'Invalid email or password. Please check your credentials and try again.',
          };
        }
        if (error.message.includes('Email not confirmed')) {
          return {
            role: null,
            error: 'Your email address has not been verified. Please check your inbox.',
          };
        }
        if (error.message.includes('network') || error.message.includes('fetch')) {
          return {
            role: null,
            error: 'Network error. Please check your internet connection and try again.',
          };
        }
        return { role: null, error: error.message };
      }

      if (!data.user) {
        return { role: null, error: 'Sign in failed. Please try again.' };
      }

      // Server-side profile fetch — bypasses RLS, no cookie race
      const { profile, error: profileError } = await fetchProfile(email);

      if (profileError || !profile) {
        // No silent default — surface the error
        return {
          role: null,
          error: profileError || 'Account not provisioned',
        };
      }

      const rolesArray: UserRole[] =
        Array.isArray(profile.user_roles) && profile.user_roles.length > 0
          ? (profile.user_roles as UserRole[])
          : profile.user_role
            ? [profile.user_role as UserRole]
            : [];

      if (rolesArray.length === 0) {
        return {
          role: null,
          error: 'Account has no assigned roles. Contact your administrator.',
        };
      }

      const primaryRole = rolesArray[0];

      setCurrentUser({
        id: profile.id || data.user.id,
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || email,
        role: primaryRole,
        roles: rolesArray,
        profile_image: profile.profile_image || '',
        assigned_pm_id: profile.assigned_pm_id || profile.id || data.user.id,
      });

      setIsAuthenticated(true);
      return { role: primaryRole, error: null };
    } catch (err: any) {
      if (err?.message?.includes('fetch') || err?.message?.includes('network')) {
        return {
          role: null,
          error: 'Network error. Please check your internet connection and try again.',
        };
      }
      return { role: null, error: 'An unexpected error occurred. Please try again.' };
    } finally {
      signingInRef.current = false;
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Ignore Supabase signOut errors — state will still be cleared
    } finally {
      setCurrentUser(EMPTY_USER);
      setIsAuthenticated(false);
    }
  };

  // Multi-role helpers — Admin/Manager (ALL_ACCESS) satisfy every role check
  const isAllAccess = currentUser.roles.some(r => ALL_ACCESS_ROLES.includes(r));

  const hasRole = (role: UserRole): boolean => {
    return currentUser.roles.includes(role) || isAllAccess;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (isAllAccess) return true;
    return roles.some(r => currentUser.roles.includes(r));
  };

  // Legacy computed booleans (backward compat)
  // 'isSuperAdmin' now means "Admin" — the system-administration role.
  const isSuperAdmin = currentUser.roles.includes('Admin');
  const isProjectManager = isAllAccess;
  const isInstallerCrew = false;

  const canSeeExecutive = isAllAccess;
  const canSeeFinancial = isAllAccess;
  const canSeeOperations = isAllAccess;

  // Effective identity — plain currentUser values here; components that need
  // impersonation-aware values should call useImpersonation() alongside useAuth()
  // and derive: getEffectiveUserId(currentUser.id) / getEffectiveRoles(currentUser.roles)
  const effectiveUserId: string | null = currentUser.id || null;
  const effectiveRoles: UserRole[] = currentUser.roles;

  const value: AuthContextType = {
    currentUser,
    setCurrentUser,
    isSuperAdmin,
    isProjectManager,
    isInstallerCrew,
    canSeeExecutive,
    canSeeFinancial,
    canSeeOperations,
    hasRole,
    hasAnyRole,
    isAuthenticated,
    authLoading,
    signIn,
    signOut,
    effectiveUserId,
    effectiveRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
